import {
  ProductPurchaseOption,
  ProductPurchaseType,
} from 'content/products';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { isMCFCountry } from 'lib/region/countryUtils';
import {
  supportedLocales,
  productToLocaleMapper,
} from 'components/m1/constants';
import { M1_Euro, NON_MCF_M1_Euro } from 'content/products/m1/EU';
import { newM1Data as newM1DataUS } from 'content/products/m1/US';
import { newM1Data as newM1DataIN } from 'content/products/m1/IN';
import { newM1Data as newM1DataAE } from 'content/products/m1/AE';
import { newM1Data as newM1DataGB } from 'content/products/m1/GB';

const ENABLE_SPECIAL_PRICES_ON_PRICING = false;

// US /pricing opens on the Subscription (membership) tab by default instead of
// One Time Purchase. US-only; flip to false to revert. Must keep defaultProduct
// and defaultProductOption in sync (see US branch in getM1Config).
const ENABLE_US_MEMBERSHIP_DEFAULT = true;

const getPricingProducts = <T extends Record<string, unknown>>(products: T): T => {
  if (ENABLE_SPECIAL_PRICES_ON_PRICING) return products;

  const { [ProductPurchaseOption.x]: _specialPrices, ...filteredProducts } =
    products;
  return filteredProducts as T;
};

const getDefaultProductOption = (products: Record<string, unknown>) =>
  ENABLE_SPECIAL_PRICES_ON_PRICING && products[ProductPurchaseOption.x]
    ? ProductPurchaseOption.x
    : ProductPurchaseOption.oneTimePurchase;

// ---------------------------------------------------------------------------
// Country-specific M1 product data & defaults
// ---------------------------------------------------------------------------

export function getM1Config(region: string) {
  const regionLower = region.toLowerCase();

  switch (region) {
    case 'US': {
      const baseUsProducts = getPricingProducts(newM1DataUS);
      // When the membership default is on, also render the Subscription tab
      // first (object key order drives tab order in BuySection). Re-spreading
      // puts the membership key in first position; the trailing spread keeps
      // every other option (and overwrites the placeholder with the real value).
      const usProducts = ENABLE_US_MEMBERSHIP_DEFAULT
        ? {
            [ProductPurchaseOption.membership]:
              baseUsProducts[ProductPurchaseOption.membership],
            ...baseUsProducts,
          }
        : baseUsProducts;
      return {
        products: usProducts,
        currency: Currency.usd,
        store: ShopifyStore.US,
        // Both must change together: an option without its matching default
        // product makes the price lookup fall back to 0 and breaks checkout.
        defaultProduct: ENABLE_US_MEMBERSHIP_DEFAULT
          ? ProductPurchaseType.membership_1_sensor_month_us
          : ProductPurchaseType.otp_3_month_us,
        defaultProductOption: ENABLE_US_MEMBERSHIP_DEFAULT
          ? ProductPurchaseOption.membership
          : getDefaultProductOption(usProducts),
        outOfStock: false,
      };
    }
    case 'IN': {
      const inProducts = getPricingProducts(newM1DataIN);
      return {
        products: inProducts,
        currency: Currency.inr,
        store: ShopifyStore.IN,
        defaultProduct: ProductPurchaseType.otp_12_week,
        defaultProductOption: getDefaultProductOption(inProducts),
      };
    }
    case 'AE': {
      const aeProducts = getPricingProducts(newM1DataAE);
      return {
        products: aeProducts,
        currency: Currency.aed,
        store: ShopifyStore.AE,
        defaultProduct: ProductPurchaseType.otp_3_month_ae,
        defaultProductOption: getDefaultProductOption(aeProducts),
        outOfStock: false,
      };
    }
    case 'GB': {
      const gbProducts = getPricingProducts(newM1DataGB);
      return {
        products: gbProducts,
        currency: Currency.gbp,
        store: ShopifyStore.GB,
        defaultProduct: ProductPurchaseType.otp_4_weeks_eu,
        defaultProductOption: getDefaultProductOption(gbProducts),
      };
    }
    default: {
      // Use supportedLocales from m1 constants for MCF/NON-MCF locales
      const localeData = supportedLocales[regionLower];
      if (localeData) {
        const isMcf = isMCFCountry(region);
        const products = getPricingProducts(
          isMcf ? M1_Euro : productToLocaleMapper[regionLower] ?? NON_MCF_M1_Euro,
        );
        return {
          products,
          currency: localeData.currency,
          store: isMcf ? ShopifyStore.EU : ShopifyStore.ROW,
          defaultProduct: localeData.defaultProduct,
          defaultProductOption: getDefaultProductOption(products),
          mcfDeliveryEstimate: isMcf,
          locale: regionLower,
        };
      }
      // Unknown region — not available
      return null;
    }
  }
}
